import { useState, useRef, useEffect } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, X, Image, Move } from "lucide-react";
import { toast } from "sonner";

const BannerImageUpload = ({ currentImage, bannerColor, themeColor, onImageUpdate }) => {
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [imgSrc, setImgSrc] = useState("");
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Dosya boyutu kontrolü (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Dosya boyutu 10MB'dan küçük olmalı");
        return;
      }

      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImgSrc(reader.result?.toString() || "");
        setShowCropDialog(true);
      });
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    // Banner için geniş aspect ratio (3:1)
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 100,
        },
        3, // aspect ratio width
        width,
        height
      ),
      width,
      height
    );
    setCrop(crop);
  };

  const getCroppedImg = async () => {
    if (!completedCrop || !imgRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Banner boyutu (1200x400 - 3:1 ratio)
    canvas.width = 1200;
    canvas.height = 400;
    
    const ctx = canvas.getContext("2d");
    
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(null);
            return;
          }
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            resolve(reader.result);
          };
        },
        "image/jpeg",
        0.85 // Kalite - boyutu küçültmek için
      );
    });
  };

  const handleSaveCrop = async () => {
    setUploading(true);
    try {
      const croppedImage = await getCroppedImg();
      if (croppedImage) {
        onImageUpdate(croppedImage);
        toast.success("Banner fotoğrafı güncellendi");
      }
      setShowCropDialog(false);
      setImgSrc("");
    } catch (error) {
      console.error("Crop error:", error);
      toast.error("Fotoğraf işlenirken hata oluştu");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    onImageUpdate("");
    toast.success("Banner fotoğrafı kaldırıldı");
  };

  return (
    <div>
      {/* Gizli file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onSelectFile}
      />
      
      {/* Tıklanabilir banner alanı */}
      <div 
        onClick={() => fileInputRef.current?.click()}
        className="relative cursor-pointer group rounded-lg overflow-hidden border-2 border-dashed border-gray-300 hover:border-emerald-500 transition-colors"
        style={{ height: '140px' }}
      >
        {currentImage && currentImage.length > 0 ? (
          <>
            <img 
              src={currentImage} 
              alt="Banner" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
              <div className="text-white text-center">
                <Upload className="h-8 w-8 mx-auto mb-1" />
                <span className="text-sm">Değiştir</span>
              </div>
            </div>
          </>
        ) : (
          <div 
            className="w-full h-full flex flex-col items-center justify-center relative"
            style={{ backgroundColor: bannerColor || themeColor || '#10b981' }}
          >
            <div className="bg-white rounded-lg p-4 text-center shadow-lg border">
              <Image className="h-10 w-10 mx-auto mb-2 text-emerald-600" />
              <span className="text-sm text-gray-700 font-medium block">Banner fotoğrafı yükle</span>
              <span className="text-xs text-gray-500 mt-1 block">Tıklayarak seçin, kırpma yapabilirsiniz</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Kaldır butonu */}
      {currentImage && currentImage.length > 0 && (
        <Button 
          type="button" 
          variant="outline" 
          size="sm"
          className="mt-2"
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveImage();
          }}
        >
          <X className="h-4 w-4 mr-1" />
          Fotoğrafı Kaldır
        </Button>
      )}

      {/* Kırpma Dialog'u */}
      <Dialog open={showCropDialog} onOpenChange={setShowCropDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Move className="h-5 w-5" />
              Banner Fotoğrafını Kırp ve Konumlandır
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-500 mb-4">
              Fotoğrafı sürükleyerek konumlandırın. Köşelerden çekerek kırpma alanını ayarlayın.
            </p>
            
            {imgSrc && (
              <div className="max-h-[400px] overflow-auto bg-gray-100 rounded-lg p-2">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={3}
                  minHeight={100}
                >
                  <img
                    ref={imgRef}
                    alt="Crop"
                    src={imgSrc}
                    onLoad={onImageLoad}
                    style={{ maxHeight: '400px', width: 'auto' }}
                  />
                </ReactCrop>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCropDialog(false);
                setImgSrc("");
              }}
            >
              İptal
            </Button>
            <Button 
              onClick={handleSaveCrop} 
              disabled={uploading || !completedCrop}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {uploading ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BannerImageUpload;
